import { authService } from '@/services/auth.service';
import { sysadminService } from '@/services/sysadmin.service';
import type { DecodedJwtPayload, OriginalUserData } from '@/types/impersonation';
import type { UserRole } from '@/types/types';
import { type ReactNode, useEffect, useState } from 'react';
import { getDefaultPathForRole } from './redirects';
import { AuthContext, type AuthUser } from './useAuth';

const API_URL = import.meta.env.VITE_API_BASE_URL;
const ORIGINAL_USER_KEY = 'tourixia_original_user';

function clearAuthKeys() {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('auth_user');
  localStorage.removeItem(ORIGINAL_USER_KEY);
}

/**
 * Decodifica el payload de un JWT sin verificar la firma.
 * Solo se usa para leer el contenido, no para validar.
 */
function decodeJwtPayload(token: string): DecodedJwtPayload | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedBy, setImpersonatedBy] = useState<number | null>(null);
  const [originalUser, setOriginalUser] = useState<OriginalUserData | null>(null);
  const hasStoredToken = !!localStorage.getItem('auth_token');
  const [isValidating, setIsValidating] = useState(hasStoredToken);

  // Al montar, verificar si hay una impersonación guardada
  useEffect(() => {
    const saved = localStorage.getItem(ORIGINAL_USER_KEY);
    if (saved) {
      try {
        const data: OriginalUserData = JSON.parse(saved);
        // Verificar que no haya expirado (24h)
        const now = Date.now();
        if (now - data.timestamp > 24 * 60 * 60 * 1000) {
          localStorage.removeItem(ORIGINAL_USER_KEY);
        } else {
          setOriginalUser(data);
          setIsImpersonating(true);
        }
      } catch {
        localStorage.removeItem(ORIGINAL_USER_KEY);
      }
    }
  }, []);

  // Validar token con el backend al cargar
  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');

    if (!storedToken) {
      setIsValidating(false);
      return;
    }

    const storedUser = localStorage.getItem('auth_user');

    authService
      .validate(storedToken)
      .then((res) => {
        if (res.valid) {
          setToken(storedToken);
          if (storedUser) {
            setUser(JSON.parse(storedUser));
          }

          // Leer flags de impersonación del token
          const payload = decodeJwtPayload(storedToken);
          if (payload?.isImpersonating) {
            setIsImpersonating(true);
            setImpersonatedBy(payload.impersonatedBy || null);
          }
        } else {
          clearAuthKeys();
          setIsImpersonating(false);
        }
      })
      .catch(() => {
        clearAuthKeys();
        setIsImpersonating(false);
      })
      .finally(() => setIsValidating(false));
  }, []);

  // Escuchar cambios en otras pestañas (Cross-Tab sync)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Si otra tab cierra sesión o el token se invalida
      if (e.key === 'auth_token' && e.newValue === null) {
        setToken(null);
        setUser(null);
        setIsImpersonating(false);
        setImpersonatedBy(null);
        setOriginalUser(null);
      }

      // Si otra tab inicia sesión
      if (e.key === 'auth_token' && e.newValue !== null) {
        const newUserStr = localStorage.getItem('auth_user');
        if (newUserStr) {
          setToken(e.newValue);
          setUser(JSON.parse(newUserStr));
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const login = async (idToken: string) => {
    try {
      const data = await authService.login(idToken);

      const authToken = data.token;
      const authUser: AuthUser = {
        id: data.user.id,
        email: data.user.email,
        name: data.user.name,
        picture: data.user.picture,
        asesor: data.user.asesor ? data.user.asesor : undefined,
      };

      localStorage.setItem('auth_token', authToken);
      localStorage.setItem('auth_user', JSON.stringify(authUser));
      setToken(authToken);
      setUser(authUser);

      // Limpiar datos de impersonación al login normal
      localStorage.removeItem(ORIGINAL_USER_KEY);
      setIsImpersonating(false);
      setImpersonatedBy(null);
      setOriginalUser(null);

      // Redirigir a la última ruta guardada si existe
      const lastPath = sessionStorage.getItem('last_path');
      if (lastPath) {
        sessionStorage.removeItem('last_path');
        window.location.replace(lastPath);
      } else {
        const role = authUser.asesor?.rol as UserRole | undefined;
        window.location.replace(getDefaultPathForRole(role));
      }
    } catch (err) {
      console.error('Error en login:', err);
      throw err;
    }
  };

  const localLogin = async (email: string) => {
    try {
      const data = await authService.localLogin(email);

      const authToken = data.token;
      const authUser: AuthUser = {
        id: data.user.id,
        email: data.user.email,
        name: data.user.name,
        picture: data.user.picture,
        asesor: data.user.asesor ? data.user.asesor : undefined,
      };

      localStorage.setItem('auth_token', authToken);
      localStorage.setItem('auth_user', JSON.stringify(authUser));
      setToken(authToken);
      setUser(authUser);

      // Limpiar datos de impersonación al login normal
      localStorage.removeItem(ORIGINAL_USER_KEY);
      setIsImpersonating(false);
      setImpersonatedBy(null);
      setOriginalUser(null);

      // Redirigir a la última ruta guardada si existe
      const lastPath = sessionStorage.getItem('last_path');
      if (lastPath) {
        sessionStorage.removeItem('last_path');
        window.location.replace(lastPath);
      } else {
        const role = authUser.asesor?.rol as UserRole | undefined;
        window.location.replace(getDefaultPathForRole(role));
      }
    } catch (err) {
      console.error('Error en login:', err);
      throw err;
    }
  };

  const impersonate = async (asesorId: number) => {
    try {
      // 1. Guardar sesión actual ANTES de cambiar (solo si no estamos ya impersonando)
      if (!localStorage.getItem(ORIGINAL_USER_KEY)) {
        const currentOriginal: OriginalUserData = {
          token: localStorage.getItem('auth_token')!,
          userId: user?.id!,
          userEmail: user?.email!,
          userRole: user?.asesor?.rol || 'SYSADMIN',
          timestamp: Date.now(),
        };
        localStorage.setItem(ORIGINAL_USER_KEY, JSON.stringify(currentOriginal));
        setOriginalUser(currentOriginal);
      }

      // 2. Llamar al backend para obtener token de impersonación
      const response = await sysadminService.impersonateAsesor(asesorId);

      // 3. Reemplazar sesión con el nuevo token
      const newToken = response.token;
      localStorage.setItem('auth_token', newToken);

      // 4. Actualizar estado
      setIsImpersonating(true);
      setImpersonatedBy(response.impersonation.impersonatedBy);

      const storedOriginal = localStorage.getItem(ORIGINAL_USER_KEY);
      if (storedOriginal) {
        setOriginalUser(JSON.parse(storedOriginal));
      }

      // 5. Decodificar nuevo token y actualizar usuario
      const newPayload = decodeJwtPayload(newToken);
      if (newPayload) {
        const updatedUser: AuthUser = {
          id: String(newPayload.id),
          email: newPayload.email,
          name: response.impersonation.asesorEmail,
          picture: user?.picture || '',
          asesor: user?.asesor ? { ...user.asesor, id: String(asesorId), rol: newPayload.role } : undefined,
        };
        setUser(updatedUser);
        localStorage.setItem('auth_user', JSON.stringify(updatedUser));
      }

      setToken(newToken);
    } catch (err) {
      console.error('Error al iniciar impersonación:', err);
      throw err;
    }
  };

  const revertImpersonation = async () => {
    try {
      // 1. Llamar al backend para obtener token limpio del sysadmin
      const response = await sysadminService.revertImpersonation();

      // 2. Restaurar sesión
      const newToken = response.token;
      localStorage.setItem('auth_token', newToken);

      // 3. Limpiar datos de impersonación
      localStorage.removeItem(ORIGINAL_USER_KEY);
      setIsImpersonating(false);
      setImpersonatedBy(null);
      setOriginalUser(null);

      // 4. Actualizar identidad al sysadmin original
      const newPayload = decodeJwtPayload(newToken);
      if (newPayload && originalUser) {
        const updatedUser: AuthUser = {
          id: originalUser.userId,
          email: originalUser.userEmail,
          name: user?.name || originalUser.userEmail,
          picture: user?.picture || '',
          asesor: user?.asesor ? { ...user.asesor, rol: originalUser.userRole } : undefined,
        };
        setUser(updatedUser);
        localStorage.setItem('auth_user', JSON.stringify(updatedUser));
      }

      setToken(newToken);
    } catch (err) {
      console.error('Error al revertir impersonación:', err);
      throw err;
    }
  };

  const switchToAdmin = async () => {
    try {
      // 1. Guardar sesión actual (solo si no estamos ya impersonando)
      if (!localStorage.getItem(ORIGINAL_USER_KEY)) {
        const currentOriginal: OriginalUserData = {
          token: localStorage.getItem('auth_token')!,
          userId: user?.id!,
          userEmail: user?.email!,
          userRole: user?.asesor?.rol || 'SYSADMIN',
          timestamp: Date.now(),
        };
        localStorage.setItem(ORIGINAL_USER_KEY, JSON.stringify(currentOriginal));
        setOriginalUser(currentOriginal);
      }

      // 2. Llamar al backend
      const response = await sysadminService.switchToAdmin();

      // 3. Reemplazar sesión
      const newToken = response.token;
      localStorage.setItem('auth_token', newToken);

      // 4. Actualizar estado
      setIsImpersonating(true);

      const storedOriginal = localStorage.getItem(ORIGINAL_USER_KEY);
      if (storedOriginal) {
        setOriginalUser(JSON.parse(storedOriginal));
      }

      // 5. Actualizar usuario (rol ADMIN)
      const updatedUser: AuthUser = {
        ...user!,
        asesor: user?.asesor ? { ...user.asesor, rol: 'ADMIN' } : undefined,
      };
      setUser(updatedUser);
      localStorage.setItem('auth_user', JSON.stringify(updatedUser));

      setToken(newToken);
    } catch (err) {
      console.error('Error al cambiar a ADMIN:', err);
      throw err;
    }
  };

  const logout = async () => {
    // Si hay impersonación activa, revertir en vez de cerrar sesión
    if (isImpersonating && originalUser) {
      try {
        await revertImpersonation();
        return;
      } catch (err) {
        console.error('Error al revertir:', err);
      }
    }

    authService.logout().finally(() => {
      clearAuthKeys();
      setToken(null);
      setUser(null);
      setIsImpersonating(false);
      setImpersonatedBy(null);
      setOriginalUser(null);
    });
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        isAuthenticated: !!token,
        isValidating,
        login,
        localLogin,
        logout,
        isImpersonating,
        impersonatedBy,
        originalUser,
        impersonate,
        revertImpersonation,
        switchToAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
